import { Loading } from "../../app/Status";
import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DOC_CATEGORIES,
  DOC_CATEGORY_LABELS,
  DOC_DEFAULT_REMIND_DAYS,
  DOC_VISIBILITIES,
  DOC_VISIBILITY_LABELS,
  type DocCategory,
  type DocumentTypeDto,
  type DocVisibility,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { IconPencil, IconPlus } from "../../app/icons";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { errMsg } from "./DossierDialogs";
import { docTypesKey, useDocTypes } from "./useDossier";

const validity = (m: number | null) => (!m ? "Onbeperkt" : m % 12 === 0 ? `${m / 12} jaar` : `${m} maanden`);

/**
 * Organisatie › Documentsoorten: de vaste soorten per rubriek, met geldigheid, herinnering en zichtbaarheid.
 * Rubrieken zelf liggen vast; soorten kunnen erbij of uit.
 */
export function DocTypesSection({ canEdit }: { canEdit: boolean }) {
  const types = useDocTypes();
  const [edit, setEdit] = useState<DocumentTypeDto | "new" | null>(null);
  if (!types.data) return types.isError ? <p className="muted">{errMsg(types.error)}</p> : <Loading />;
  return (
    <section className="panel" aria-label="Documentsoorten">
      <div className="row">
        <p className="muted small grow">
          Elk document in het dossier heeft een soort. De soort bepaalt de rubriek, hoe lang een document geldig is,
          wanneer de herinnering komt en wie het standaard mag zien.
        </p>
        {canEdit && (
          <button type="button" className="btn small" onClick={() => setEdit("new")}>
            <IconPlus size={18} aria-hidden="true" /> Soort
          </button>
        )}
      </div>
      {DOC_CATEGORIES.map((c) => {
        const list = types.data.filter((t) => t.category === c);
        if (!list.length) return null;
        return (
          <div key={c} className="tscroll">
            <h3 className="doctype-cat">{DOC_CATEGORY_LABELS[c]}</h3>
            <table className="it" aria-label={DOC_CATEGORY_LABELS[c]}>
              <thead>
                <tr>
                  <th scope="col">Soort</th>
                  <th scope="col">Geldig</th>
                  <th scope="col" className="dir-hide-narrow">
                    Herinnering
                  </th>
                  <th scope="col" className="dir-hide-narrow">
                    Zichtbaar
                  </th>
                  <th scope="col" className="num">
                    In gebruik
                  </th>
                  {canEdit && (
                    <th scope="col">
                      <span className="sr-only">Bewerken</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} className={t.active ? "" : "muted"}>
                    <td>
                      {t.name}
                      {t.drawing && <span className="muted small"> · tekening</span>}
                      {!t.active && <span className="muted small"> · uit</span>}
                    </td>
                    <td>{validity(t.validityMonths)}</td>
                    <td className="dir-hide-narrow">
                      {t.validityMonths ? `${t.remindDays ?? DOC_DEFAULT_REMIND_DAYS} dagen vooraf` : "–"}
                    </td>
                    <td className="dir-hide-narrow">
                      {t.confidential ? "Altijd vertrouwelijk" : DOC_VISIBILITY_LABELS[t.visibility]}
                    </td>
                    <td className="num">{t.documentCount}</td>
                    {canEdit && (
                      <td>
                        <button
                          type="button"
                          className="btn ghost ico small"
                          aria-label={`${t.name} bewerken`}
                          onClick={() => setEdit(t)}
                        >
                          <IconPencil size={18} aria-hidden="true" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
      {edit && <TypeDialog type={edit === "new" ? null : edit} onClose={() => setEdit(null)} />}
    </section>
  );
}

function TypeDialog({ type, onClose }: { type: DocumentTypeDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({
    category: (type?.category ?? "technisch") as DocCategory,
    name: type?.name ?? "",
    validityMonths: type?.validityMonths ? String(type.validityMonths) : "",
    remindDays: type?.remindDays ? String(type.remindDays) : "",
    visibility: (type?.visibility ?? "intern") as DocVisibility,
    confidential: type?.confidential ?? false,
    drawing: type?.drawing ?? false,
    active: type?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      category: f.category,
      name: f.name.trim(),
      validityMonths: f.validityMonths ? Number(f.validityMonths) : null,
      remindDays: f.remindDays ? Number(f.remindDays) : null,
      visibility: f.visibility,
      confidential: f.confidential,
      drawing: f.drawing,
      active: f.active,
    };
    try {
      await api(type ? `/document-types/${type.id}` : "/document-types", {
        method: type ? "PATCH" : "POST",
        body,
      });
      await qc.invalidateQueries({ queryKey: docTypesKey });
      toast("Documentsoort opgeslagen");
      onClose();
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };
  const drawingLocked = !!type && type.documentCount > 0;
  return (
    <Modal
      title={type ? "Documentsoort bewerken" : "Nieuwe documentsoort"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || !f.name.trim()}>
            Opslaan
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void save(e)}>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <div className="grid2">
          <label className="field">
            <span className="label">Naam *</span>
            <input value={f.name} onChange={(e) => set({ name: e.target.value })} />
          </label>
          <label className="field">
            <span className="label">Rubriek</span>
            <select value={f.category} onChange={(e) => set({ category: e.target.value as DocCategory })}>
              {DOC_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {DOC_CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="grid2">
          <label className="field">
            <span className="label">Geldig (maanden)</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={600}
              value={f.validityMonths}
              onChange={(e) => set({ validityMonths: e.target.value })}
              placeholder="Onbeperkt"
            />
          </label>
          <label className="field">
            <span className="label">Herinnering (dagen vooraf)</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={730}
              value={f.remindDays}
              onChange={(e) => set({ remindDays: e.target.value })}
              placeholder={String(DOC_DEFAULT_REMIND_DAYS)}
              disabled={!f.validityMonths}
            />
          </label>
        </div>
        <label className="field">
          <span className="label">Standaard zichtbaar voor</span>
          <select
            value={f.visibility}
            onChange={(e) => set({ visibility: e.target.value as DocVisibility })}
            disabled={f.confidential}
          >
            {DOC_VISIBILITIES.map((v) => (
              <option key={v} value={v}>
                {DOC_VISIBILITY_LABELS[v]}
              </option>
            ))}
          </select>
        </label>
        <label className="check">
          <input type="checkbox" checked={f.confidential} onChange={(e) => set({ confidential: e.target.checked })} />{" "}
          Altijd vertrouwelijk (bijvoorbeeld huurovereenkomsten)
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={f.drawing}
            disabled={drawingLocked}
            onChange={(e) => set({ drawing: e.target.checked })}
          />{" "}
          Tekening (met nummer, revisie en discipline)
          {drawingLocked && <span className="muted small"> · vast zodra er documenten van deze soort zijn</span>}
        </label>
        <label className="check">
          <input type="checkbox" checked={f.active} onChange={(e) => set({ active: e.target.checked })} /> In gebruik
          (uit = niet meer te kiezen bij uploaden)
        </label>
      </form>
    </Modal>
  );
}
